'use client';

import type React from 'react';
import { useCallback } from 'react';
import { Button, Table } from 'reactstrap';
import moment from 'moment';

import { formatCurrency } from 'src/helpers/utils';
import { updateUrlForOpenJobSidePanel } from 'src/helpers/job-slide-out';
import { useAppSelector } from 'src/hooks/redux-hooks';

import { PdfIcon } from 'src/assets/images/job-details-slide-out/job-details-slide-out';
import { IJobs } from 'src/types/jobs';

interface RootJob {
  root_node_id?: string;
  root_job_no?: string;
  [key: string]: unknown;
}

interface InvoiceRow {
  invoice_no?: string;
  job_id?: string;
  primaryKey?: string;
  root_jobs?: RootJob[];
  status?: { slug?: string; label?: string };
  amount?: number;
  due_date?: string;
  share_url?: string;
  [key: string]: unknown;
}

interface InvoiceColumn {
  key: string;
  label: string;
  render: (row: IJobs) => React.ReactNode;
  tableTdClass?: string;
}

const invoiceStatus: Record<string, string> = {
  paid: 'completed',
  'partially-paid': 'partially-paid',
  'past-due': 'past-due',
  'payment-skipped': 'payment-skipped',
  refunded: 'refunded',
  unpaid: 'unpaid',
  voided: 'voided',
};
const Invoices: React.FC = () => {
  const srId = useAppSelector((state) => state?.jobDetails?.slideOut?.srId);
  const invoices = useAppSelector(
    (state) => state?.jobDetails?.data?.invoices,
  );

  const invoiceColumns = useCallback((): InvoiceColumn[] => ([
    {
      key: 'invoice_no',
      label: 'Invoice #',
      render: (row) => row?.invoice_no || '',
    },
    {
      key: 'job_no',
      label: 'Lula Job #',
      render: (row) => (
        Array.isArray(row?.root_jobs) ? row?.root_jobs?.map((rootJob) => (
          <Button
            key={rootJob?.root_node_id}
            className="btn-link"
            color="btn-deoco"
            onClick={() => updateUrlForOpenJobSidePanel(
              rootJob?.root_job_no ?? '',
              'jobDetails',
              rootJob?.root_node_id ?? '',
              srId ?? '',
            )}
          >
            {rootJob?.root_job_no || ''}
          </Button>
        )) : ''
      ),
    },
    {
      key: 'status',
      label: 'Status',
      render: (row) => (
        <div className={`status ${invoiceStatus?.[row?.status?.slug || '']}`}>
          {row?.status?.label || ''}
        </div>
      ),
    },
    {
      key: 'amount',
      label: 'Amount',
      render: (row) => formatCurrency(Number(row?.amount) || 0, 2, 2) || '',
    },
    {
      key: 'due_date',
      label: 'Due Date',
      render: (row) => (row?.due_date
        ? (moment.utc(
          row?.due_date,
        ).local()?.format('MMM DD, YYYY'))
        : '-'),
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (row) => (row?.share_url
        ? (
          <Button
            type="button"
            className="pdf-btn"
            color="btn-deoco"
            onClick={() => {
              if(row?.share_url) {
                window.open(row?.share_url, '_blank');
              }
            }}
          >
            <PdfIcon />
            PDF
          </Button>
        ) : '-'),
      tableTdClass: 'job-invoice',
    },
  ]), [srId]);

  return (
    invoices?.length
      ? (
        <Table responsive className="job-content-table">
          <thead>
            <tr>
              {invoiceColumns().map((column) => (
                <th key={column?.key}>
                  {column?.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Array?.isArray(invoices)
              ? invoices?.map((rowData) => (
                <tr key={rowData?.job_id}>
                  {invoiceColumns()?.map((column) => (
                    <td
                      key={`${column?.key}`}
                      className={
                        column?.tableTdClass || ''
                      }
                    >
                      {column?.render(rowData)}
                    </td>
                  ))}
                </tr>
              )) : ''}
          </tbody>
        </Table>
      ) : (
        <div className="no-data mt-3">
          No invoices have been submitted for this job
        </div>
      )
  );
};

export default Invoices;
